// API client for the marketplace customer embed.
//
// Unlike the portal client (portal/api/client.ts) which sends
// credentials: 'include' for cookie-based sessions, this client
// sends credentials: 'omit' and injects a JWT Bearer token via
// a registered provider function. On 401 the embed clears the
// in-memory JWT and shows a sign-in prompt — no router redirect.

import type { CatalogFacets, CatalogPage } from "./types";

/**
 * ApiError models any non-2xx response in a typed shape. Thrown by
 * embedApiFetch so components can branch on status/code.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isNotImplemented(): boolean {
    return this.status === 501;
  }
}

// Token provider registered by AuthProvider on mount.
let tokenProvider: (() => string | null) | null = null;

// 401 listener registered by AuthProvider to clear JWT state.
let onUnauthenticated: (() => void) | null = null;

// Refresh provider registered by AuthProvider (TD-366). On a 401 the client
// attempts ONE silent renewal before declaring the session dead: the provider
// POSTs the refresh endpoint with the stored opaque token, persists the
// ROTATED pair, and returns the fresh access JWT (null on any failure).
// Single-flight: concurrent 401s share one in-flight refresh so a rotated
// (single-use) token is never consumed twice by parallel retries.
let refreshProvider: (() => Promise<string | null>) | null = null;
let refreshInFlight: Promise<string | null> | null = null;

/** Called by AuthProvider to register the silent-renewal provider (TD-366). */
export function setRefreshProvider(fn: (() => Promise<string | null>) | null): void {
  refreshProvider = fn;
  refreshInFlight = null;
}

async function tryRefresh(): Promise<string | null> {
  if (!refreshProvider) return null;
  if (!refreshInFlight) {
    refreshInFlight = refreshProvider().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Called by AuthProvider to register the JWT provider function. */
export function setTokenProvider(fn: (() => string | null) | null): void {
  tokenProvider = fn;
}

/** Called by AuthProvider to register a 401 handler. */
export function setOnUnauthenticated(fn: (() => void) | null): void {
  onUnauthenticated = fn;
}

/**
 * Resolved base URL for storefront API calls. Inlined by Vite at
 * build time. Falls back to localhost for local dev.
 */
export const API_BASE_URL: string = (
  import.meta.env.VITE_STOREFRONT_API_BASE_URL ??
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:8080"
).replace(/\/+$/, "");

// Dev-only diagnostic: warn when localhost API base is baked into a bundle
// served over https (mixed-content will silently fail). The portal SPA has a
// build-time guard (check-portal-bundle.mjs); the customer embed bundles have
// none, so this dev-time warning catches a dev build that accidentally deploys.
//
// Wrapped in `import.meta.env.DEV` so Vite constant-folds the block to
// `if (false) { … }` in production, dead-code-eliminating the
// "http://localhost" comparison literal. Without this wrap the literal
// survives minification and trips check-portal-bundle.mjs (TD-266), forcing
// portal deploys to pass ALLOW_LOCALHOST_PORTAL=1 to bypass the guard.
if (import.meta.env.DEV) {
  if (
    API_BASE_URL.startsWith("http://localhost") &&
    typeof window !== "undefined" &&
    window.location.protocol === "https:"
  ) {
    console.warn(
      "[aldelo-embed] API base is localhost — requests will fail on https sites due to mixed-content. " +
      "Set VITE_API_BASE_URL at build time.",
    );
  }
}

export interface EmbedApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** Extra request headers merged after defaults (Accept, Authorization). */
  headers?: Record<string, string>;
  /**
   * Skip the shared onUnauthenticated listener on a 401. For requests whose
   * 401 is EXPECTED and not customer-auth-level — e.g. a WebAuthn assertion
   * the server rejects as a failed ceremony — firing the
   * listener would clear the customer JWT (auth-context) and bounce a
   * returning customer to sign-in for a reason that has nothing to do with
   * their session. Callers that opt out MUST branch on the thrown ApiError
   * themselves.
   */
  suppressUnauthenticated?: boolean;
}

/**
 * Issue a storefront API call with JWT Bearer auth.
 * Returns the unwrapped `data` payload or throws ApiError.
 */
export async function embedApiFetch<T>(
  path: string,
  opts: EmbedApiFetchOptions = {},
): Promise<T> {
  return embedApiFetchInner<T>(path, opts, false);
}

// retriedAuth guards the single TD-366 renewal attempt: a 401 triggers one
// silent refresh + one retry; a retry that 401s again falls through to the
// onUnauthenticated listener instead of looping.
async function embedApiFetchInner<T>(
  path: string,
  opts: EmbedApiFetchOptions,
  retriedAuth: boolean,
): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  const token = tokenProvider?.();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "omit",
    headers,
    signal: opts.signal,
  };

  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }

  if (opts.headers) {
    Object.assign(headers, opts.headers);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      err instanceof Error ? err.message : "Network request failed",
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const envelope = (payload as { error?: unknown } | null)?.error;
    const errObj =
      envelope && typeof envelope === "object"
        ? (envelope as Record<string, unknown>)
        : {};
    const code = typeof errObj.code === "string" ? errObj.code : "UNKNOWN";
    const message =
      typeof errObj.message === "string"
        ? errObj.message
        : `Request failed (${res.status})`;

    if (res.status === 401 && !opts.suppressUnauthenticated && !retriedAuth) {
      // TD-366: a 15-minute access JWT expiring mid-session is the NORMAL
      // case now, not an error. Attempt one silent renewal; on success the
      // recursive call re-reads the (fresh) token provider and retries.
      const fresh = await tryRefresh();
      if (fresh !== null) {
        return embedApiFetchInner<T>(path, opts, true);
      }
    }

    if (res.status === 401 && !opts.suppressUnauthenticated && onUnauthenticated) {
      try {
        onUnauthenticated();
      } catch {
        // Swallow — listener decides how to handle.
      }
    }

    throw new ApiError(res.status, code, message, errObj.details);
  }

  const envelope = (payload as { data?: unknown } | null) ?? null;
  return (envelope?.data ?? null) as T;
}

/**
 * Issue a storefront API call with the SAME auth/error plumbing as
 * embedApiFetch (JWT Bearer header, network-error mapping,
 * ApiError envelope parsing, 401 listener + suppressUnauthenticated), but
 * return the FULL response envelope instead of unwrapping `.data` — the
 * catalog list endpoint paginates via `{data, pagination, facets}` siblings
 * that the unwrapping variant cannot surface (spec §5.5).
 *
 * For non-catalog envelopes that omit the siblings, `pagination` and
 * `facets` resolve to null (never undefined), so consumers can branch with
 * a plain null check.
 */
export async function embedApiFetchPage<T>(
  path: string,
  opts: EmbedApiFetchOptions = {},
): Promise<{
  data: T;
  pagination: CatalogPage["pagination"] | null;
  facets: CatalogFacets | null;
}> {
  return embedApiFetchPageInner<T>(path, opts, false);
}

// retriedAuth: single TD-366 renewal attempt, mirroring embedApiFetchInner.
async function embedApiFetchPageInner<T>(
  path: string,
  opts: EmbedApiFetchOptions,
  retriedAuth: boolean,
): Promise<{
  data: T;
  pagination: CatalogPage["pagination"] | null;
  facets: CatalogFacets | null;
}> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  const token = tokenProvider?.();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "omit",
    headers,
    signal: opts.signal,
  };

  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }

  if (opts.headers) {
    Object.assign(headers, opts.headers);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      err instanceof Error ? err.message : "Network request failed",
    );
  }

  if (res.status === 204) {
    // Mirror embedApiFetch's 204 contract (resolves undefined); the double
    // cast is the same escape hatch it uses for `undefined as T`.
    return undefined as unknown as {
      data: T;
      pagination: CatalogPage["pagination"] | null;
      facets: CatalogFacets | null;
    };
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const envelope = (payload as { error?: unknown } | null)?.error;
    const errObj =
      envelope && typeof envelope === "object"
        ? (envelope as Record<string, unknown>)
        : {};
    const code = typeof errObj.code === "string" ? errObj.code : "UNKNOWN";
    const message =
      typeof errObj.message === "string"
        ? errObj.message
        : `Request failed (${res.status})`;

    if (res.status === 401 && !opts.suppressUnauthenticated && !retriedAuth) {
      // TD-366: one silent renewal before giving up (see embedApiFetchInner).
      const fresh = await tryRefresh();
      if (fresh !== null) {
        return embedApiFetchPageInner<T>(path, opts, true);
      }
    }

    if (res.status === 401 && !opts.suppressUnauthenticated && onUnauthenticated) {
      try {
        onUnauthenticated();
      } catch {
        // Swallow — listener decides how to handle.
      }
    }

    throw new ApiError(res.status, code, message, errObj.details);
  }

  const envelope = (payload as {
    data?: unknown;
    pagination?: unknown;
    facets?: unknown;
  } | null) ?? null;
  return {
    data: (envelope?.data ?? null) as T,
    pagination: (envelope?.pagination ?? null) as CatalogPage["pagination"] | null,
    facets: (envelope?.facets ?? null) as CatalogFacets | null,
  };
}

/**
 * Issue a storefront API call with the SAME auth/error plumbing as
 * embedApiFetch (JWT Bearer header, network-error mapping,
 * ApiError envelope parsing, 401 listener + suppressUnauthenticated, one
 * TD-366 silent-renewal retry), but return the FULL response body as T —
 * no `.data` unwrap. The portal client's apiFetchRaw mirror, added for
 * endpoints whose envelope carries siblings the unwrap would drop (the T25
 * notifications list: `{"data": [...], "unread": N}`). Non-catalog by
 * design — pagination/facets consumers use embedApiFetchPage.
 */
export async function embedApiFetchRaw<T>(
  path: string,
  opts: EmbedApiFetchOptions = {},
): Promise<T> {
  return embedApiFetchRawInner<T>(path, opts, false);
}

// retriedAuth: single TD-366 renewal attempt, mirroring embedApiFetchInner.
async function embedApiFetchRawInner<T>(
  path: string,
  opts: EmbedApiFetchOptions,
  retriedAuth: boolean,
): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  const token = tokenProvider?.();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "omit",
    headers,
    signal: opts.signal,
  };

  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }

  if (opts.headers) {
    Object.assign(headers, opts.headers);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      err instanceof Error ? err.message : "Network request failed",
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const envelope = (payload as { error?: unknown } | null)?.error;
    const errObj =
      envelope && typeof envelope === "object"
        ? (envelope as Record<string, unknown>)
        : {};
    const code = typeof errObj.code === "string" ? errObj.code : "UNKNOWN";
    const message =
      typeof errObj.message === "string"
        ? errObj.message
        : `Request failed (${res.status})`;

    if (res.status === 401 && !opts.suppressUnauthenticated && !retriedAuth) {
      // TD-366: one silent renewal before giving up (see embedApiFetchInner).
      const fresh = await tryRefresh();
      if (fresh !== null) {
        return embedApiFetchRawInner<T>(path, opts, true);
      }
    }

    if (res.status === 401 && !opts.suppressUnauthenticated && onUnauthenticated) {
      try {
        onUnauthenticated();
      } catch {
        // Swallow — listener decides how to handle.
      }
    }

    throw new ApiError(res.status, code, message, errObj.details);
  }

  return payload as T;
}

function buildUrl(
  path: string,
  query?: Record<string, string | number | undefined | null>,
): string {
  const base = path.startsWith("http")
    ? path
    : `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;

  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs.length === 0 ? base : `${base}?${qs}`;
}
